import { FC, useMemo, useRef, useState } from 'react';

import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useGiftCsMemoListQuery } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import { toastError } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';

import { BillingCardSection } from './couponDetail/BillingCardSection';
import { CancelConfirmDialog } from './couponDetail/CancelConfirmDialog';
import { CancelSection } from './couponDetail/CancelSection';
import {
  CouponDetailHeader,
  CouponHeaderAction,
} from './couponDetail/CouponDetailHeader';
import { CouponShareHistoryModal } from './couponDetail/CouponShareHistoryModal';
import { CouponTable } from './couponDetail/CouponTable';
import { CourtesyMessageModal } from './couponDetail/CourtesyMessageModal';
import { CrossCouponOverlapBanner } from './couponDetail/CrossCouponOverlapBanner';
import { CsMemoButton, splitCsMemos } from './csMemo';
import { DisposeConfirmDialog } from './couponDetail/DisposeConfirmDialog';
import { DisposeSection } from './couponDetail/DisposeSection';
import { ExtraRedeemModal } from './couponDetail/ExtraRedeemModal';
import { ExtraFulfillModal, ExtraPayModal } from './ExtraPaymentActionModals';
import { KakaoShareCallbackModal } from './couponDetail/KakaoShareCallbackModal';
import { ManualRefundCancelDialog } from './couponDetail/ManualRefundCancelDialog';
import { PhoneFormUploadModal } from './couponDetail/PhoneFormUploadModal';
import { ProxyDeliverModal } from './couponDetail/ProxyDeliverModal';
import { IssueLinkConfirmModal, IssueLinkConfirmTarget } from './IssueLinkConfirmModal';
import { RefundLinkNoticeModal, RefundLinkNoticeTarget } from './RefundLinkNoticeModal';
import { RenewalRecordModal, RenewalRecordTarget } from './couponDetail/RenewalRecordModal';
import { ReissueSection } from './couponDetail/ReissueSection';
import { ResultBanners } from './couponDetail/ResultBanners';
import { isProxyDeliverEligible, resolveGiftItemId } from './couponDetail/constants';
import { CouponDetailModalProps } from './couponDetail/types';
import { useCouponDetailState } from './couponDetail/useCouponDetailState';
import { UsageGuideSendModal } from './couponDetail/UsageGuideSendModal';
import {
  exportDeliveredCouponPhoneExcel,
  getDeliveredCouponPhoneTargets,
  getPhoneFormTargets,
} from '../utils/deliveredCouponPhoneExcel';
import {
  parsePhoneFormWorkbook,
  PhoneFormParseResult,
} from '../utils/phoneFormExcelUpload';

export type { CouponDetailModalProps };

export const CouponDetailModal: FC<CouponDetailModalProps> = (props) => {
  const { onClose } = props;
  const { isAdmin, isGiftAgent } = useAuth();
  const state = useCouponDetailState(props);
  const sortedCoupons = state.sortedCoupons;
  const couponMap = useMemo(() => {
    const map = new Map<string, GiftCouponSummary>();
    for (const c of sortedCoupons) map.set(c._id, c);
    return map;
  }, [sortedCoupons]);

  // 환불/연장 링크 발행 확인 대상
  const issueConfirmState = state.issueConfirmTarget;
  const issueConfirmTarget = useMemo<IssueLinkConfirmTarget | null>(() => {
    if (!issueConfirmState) return null;
    const c = couponMap.get(issueConfirmState.couponId);
    return { ...issueConfirmState, label: c ? `#${c.seq} · ${c.code}` : undefined };
  }, [issueConfirmState, couponMap]);

  // 환불 링크 안내 문자 대상 — 수신번호 후보(신청번호·확인번호·주문자)를 쿠폰·주문에서 모은다
  const refundNoticeState = state.refundNoticeTarget;
  const orderReceiverPhone = props.order?.receiverPhoneNumber;
  const refundNoticeTarget = useMemo<RefundLinkNoticeTarget | null>(() => {
    if (!refundNoticeState) return null;
    const c = couponMap.get(refundNoticeState.couponId);
    return {
      couponId: refundNoticeState.couponId,
      justIssued: refundNoticeState.justIssued,
      label: c ? `#${c.seq} · ${c.code}` : undefined,
      recipientPhone: c?.recipient?.phoneNumber,
      additionalPhone: c?.additionalRecipientPhone,
      ordererPhone: orderReceiverPhone,
      lastNotice: c?.issued?.noticeSentAt
        ? {
            sentAt: c.issued.noticeSentAt,
            phone: c.issued.noticePhone,
            count: c.issued.noticeCount,
          }
        : undefined,
    };
  }, [refundNoticeState, couponMap, orderReceiverPhone]);

  // 연장 처리 대상 — 원래 발송 때의 도매가·매체, 신청번호를 입력 기본값으로 넘긴다
  const renewalRecordTargetId = state.renewalRecordTargetId;
  const giftItemEmbedMap = state.giftItemEmbedMap;
  const renewalRecordTarget = useMemo<RenewalRecordTarget | null>(() => {
    if (!renewalRecordTargetId) return null;
    const c = couponMap.get(renewalRecordTargetId);
    const giftItemId = c ? resolveGiftItemId(c.giftItem) : undefined;
    return {
      couponId: renewalRecordTargetId,
      label: c ? `#${c.seq} · ${c.code}` : undefined,
      itemName: giftItemId ? giftItemEmbedMap.get(giftItemId)?.name : undefined,
      priceWhole: c?.gifticon?.priceWhole,
      deliveryChannel: c?.gifticon?.deliveryChannel,
      recipientPhone: c?.recipient?.phoneNumber,
    };
  }, [renewalRecordTargetId, couponMap, giftItemEmbedMap]);

  // CS 응대 메모 — 주문 축으로 한 번만 조회하면 쿠폰·배송 단위 메모까지 같이 온다.
  // (virtual populate는 구서버에서 500 나고 adminOnly 메모도 안 실려서 전용 GET을 쓴다)
  const csMemoQuery = useGiftCsMemoListQuery(
    { giftOrder: props.orderId ?? '' },
    !!props.orderId,
  );
  const csMemoSplit = useMemo(() => splitCsMemos(csMemoQuery.data), [csMemoQuery.data]);
  const csMemoUnavailable = csMemoQuery.data === null;
  const csMemoSubtitle = props.order
    ? `주문자 ${props.order.receiverName ?? '-'} · ${
        props.order.receiverPhoneNumber ?? '-'
      }`
    : undefined;
  // 통합 타임라인에서 "이 메모가 어느 쿠폰/배송 건인지" 라벨
  const csMemoSubLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of sortedCoupons) map.set(c._id, `쿠폰 ${c.code}-${c.seq}`);
    for (const d of props.order?.deliveries ?? []) map.set(d._id, `배송 ${d.code}`);
    return map;
  }, [sortedCoupons, props.order]);

  // 이용안내 일괄 발송 — 주문 단위로, 신청자(수신) 번호가 있는 쿠폰들에 교환·잔액
  // 유의사항(이용안내)을 LMS로 한 번 더 고지한다. (백엔드 엔드포인트 준비 후 동작)
  const [showUsageGuideModal, setShowUsageGuideModal] = useState(false);
  // 백엔드와 동일 기준: 이미 교환(Requested/Delivered)했고 신청자 번호가 있는 쿠폰.
  const usageGuideTargets = useMemo(
    () =>
      sortedCoupons.filter(
        (c) =>
          !!c.recipient?.phoneNumber &&
          (c.redeemStatus === GiftCouponRedeemStatus.Requested ||
            c.redeemStatus === GiftCouponRedeemStatus.Delivered),
      ),
    [sortedCoupons],
  );

  // 발송완료 번호 엑셀 — 신청자 번호(나중에받기·번호인증·발송완료)가 있는 쿠폰을
  // 가운데 4자리 마스킹해 내보낸다. 대상이 없으면 버튼 비활성화.
  const deliveredPhoneTargets = useMemo(
    () => getDeliveredCouponPhoneTargets(sortedCoupons),
    [sortedCoupons],
  );
  // 미발송 포함(번호 기입 폼) — 취소·폐기 제외 전체. 미발송 건이 있을 때만 의미가 있다.
  const phoneFormTargets = useMemo(
    () => getPhoneFormTargets(sortedCoupons),
    [sortedCoupons],
  );

  // 기입폼 업로드 — 채워 받은 엑셀의 수령번호를 확인번호로 일괄 저장.
  // 저장 엔드포인트가 admin 전용(PATCH /admin/gift-coupon/:id)이라 admin만 노출.
  const phoneFormFileRef = useRef<HTMLInputElement>(null);
  const [phoneFormParsed, setPhoneFormParsed] =
    useState<PhoneFormParseResult | null>(null);
  const handlePhoneFormFile = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      setPhoneFormParsed(parsePhoneFormWorkbook(buf, sortedCoupons));
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : '엑셀 파일을 읽지 못했습니다.',
      );
    }
  };

  // 대리 일괄발송 — 수령자 신청(번호인증·서명) 없이 관리자가 연락처를 받아 주문의
  // 쿠폰들을 바로 발송완료 처리. (404 안내는 구서버 안전장치)
  const [showProxyDeliverModal, setShowProxyDeliverModal] = useState(false);
  const proxyDeliverTargets = useMemo(
    () => sortedCoupons.filter(isProxyDeliverEligible),
    [sortedCoupons],
  );

  // 헤더 액션 — 버튼 전부 열거하되 비슷한 기능끼리 그룹 박스로 모아 배치(번호·엑셀 | 발송).
  // 조건 미충족이어도 버튼을 숨기지 않고 비활성 사유를 클릭 팝오버로 안내한다.
  // role상 아예 못 쓰는 기능(권한 게이트)만 배열에서 제외.
  const { courtesyEligibleCoupons, setShowCourtesyModal } = state;
  const headerActions = useMemo<CouponHeaderAction[]>(() => {
    const admin = isAdmin();
    const agent = isGiftAgent();
    const unsentCount = phoneFormTargets.length - deliveredPhoneTargets.length;
    const actions: CouponHeaderAction[] = [];

    if (admin || agent) {
      actions.push({
        key: 'exportPhoneForm',
        label: '번호 기입폼',
        group: '번호·엑셀',
        count: Math.max(unsentCount, 0),
        hint: '미발송 쿠폰까지 순번 그대로 포함해 내보내기 — 미발송 건은 수령번호 칸에 "▶ 번호기입요망" 표시가 있어 채워서 회신받는 폼으로 쓸 수 있어요 (상태 열 필터로 미발송만 골라보기 가능)',
        disabledReason:
          unsentCount <= 0
            ? '번호를 받아야 할 미발송 쿠폰이 없습니다. 모든 쿠폰이 이미 신청·발송 상태예요.\n확보된 번호는 "발송완료 번호 엑셀"로 내보낼 수 있어요.'
            : null,
        onClick: () =>
          exportDeliveredCouponPhoneExcel(props.order, sortedCoupons, {
            includeUnsent: true,
          }),
        enabledClassName: 'border-amber-200 text-amber-700 hover:bg-amber-50',
      });
    }
    if (admin) {
      actions.push({
        key: 'uploadPhoneForm',
        label: '기입폼 업로드',
        group: '번호·엑셀',
        hint: '채워서 받은 번호 기입폼 엑셀을 올리면 수령번호는 확인번호(수신번호)로, 메모 칸(수령인 이름 등)은 쿠폰 메모로 일괄 저장 — 미리보기로 확인 후 반영·되돌리기 가능, 수신 신청됐거나 확인번호가 이미 있는 쿠폰은 번호 자동 제외(메모는 반영)',
        disabledReason:
          sortedCoupons.length === 0 ? '표시할 쿠폰이 없습니다.' : null,
        onClick: () => phoneFormFileRef.current?.click(),
        enabledClassName:
          'border-amber-300 text-amber-800 hover:bg-amber-100',
      });
    }
    if (admin || agent) {
      actions.push({
        key: 'exportDeliveredPhones',
        label: '발송완료 번호 엑셀',
        group: '번호·엑셀',
        count: deliveredPhoneTargets.length,
        hint: '신청자 번호(나중에받기·번호인증·발송완료)를 가운데 4자리 마스킹해 엑셀로 내보내기',
        disabledReason:
          deliveredPhoneTargets.length === 0
            ? '신청자 번호가 확보된 쿠폰(번호인증 신청·나중에받기·발송완료)이 아직 없습니다.'
            : null,
        onClick: () => exportDeliveredCouponPhoneExcel(props.order, sortedCoupons),
        enabledClassName:
          'border-emerald-200 text-emerald-700 hover:bg-emerald-50',
      });
    }
    if (admin || agent) {
      // 답례 인사 발송 — 신청자 확인번호(수신번호)가 있는 쿠폰이 대상. 모달 안에서
      // 수신자를 골라 보낸다(기본 전체 선택). 대상 0건이어도 버튼은 남기고 사유 안내.
      const courtesyCount = courtesyEligibleCoupons.length;
      actions.push({
        key: 'courtesy',
        label: '답례인사 발송',
        group: '발송',
        count: courtesyCount,
        hint: '[선택형] 답례글 + 답례품 받기 링크 LMS — 상품은 받는 분이 링크에서 직접 신청(선택)해요. 보낼 사람은 모달에서 골라 발송 (수신번호 있는 쿠폰, 취소건 포함·폐기건 제외)',
        disabledReason:
          courtesyCount === 0
            ? admin
              ? '수신번호(신청자 확인번호)가 채워진 쿠폰이 없습니다. 답례인사는 이 번호로 "상품을 직접 골라 신청하세요" 링크를 보내는 선택형 발송이에요.\n"번호 기입폼"으로 번호를 받아 "기입폼 업로드"로 채우면 활성화됩니다.'
              : '수신번호(신청자 확인번호)가 채워진 쿠폰이 없습니다. 답례인사는 이 번호로 "상품을 직접 골라 신청하세요" 링크를 보내는 선택형 발송이에요.\n관리자가 기입폼 업로드로 번호를 채우면 활성화됩니다.'
            : null,
        disabledCta:
          admin && sortedCoupons.length > 0
            ? {
                label: '기입폼 업로드 열기',
                onClick: () => phoneFormFileRef.current?.click(),
              }
            : undefined,
        onClick: () => setShowCourtesyModal(true),
        enabledClassName: 'border-rose-200 text-rose-700 hover:bg-rose-50',
      });
    }
    if (admin) {
      actions.push({
        key: 'proxyDeliver',
        label: '대리 일괄발송',
        group: '발송',
        count: proxyDeliverTargets.length,
        hint: '[우리가 결정] 받는 분 신청 없이 상품을 하나로 정해 발송처리까지 끝내요 (답례인사와 같은 번호 목록, 나중에받기 제외) — 받는 분이 고르게 하려면 답례인사 발송',
        disabledReason:
          proxyDeliverTargets.length === 0
            ? '대상 쿠폰이 없습니다. 대리 일괄발송은 받는 분 신청 없이 우리가 상품을 정해 발송처리까지 하는 기능이라, 신청자 확인번호(수신번호)가 채워진 미발송 쿠폰만 대상이에요.\n"번호 기입폼"으로 번호를 받아 "기입폼 업로드"로 채우면 활성화됩니다. (나중에받기 예약·환불/연장 진행·미정산 스폰 쿠폰은 제외)'
            : null,
        disabledCta:
          sortedCoupons.length > 0
            ? {
                label: '기입폼 업로드 열기',
                onClick: () => phoneFormFileRef.current?.click(),
              }
            : undefined,
        onClick: () => setShowProxyDeliverModal(true),
        enabledClassName: 'border-rose-200 text-rose-700 hover:bg-rose-50',
      });
      actions.push({
        key: 'usageGuide',
        label: '이용안내 발송',
        group: '발송',
        count: usageGuideTargets.length,
        hint: '[사후 안내] 이미 상품을 신청·받은 분들께 잘 쓰는 법(교환·잔액 유의사항) 안내 멘트 LMS — 아직 신청 전인 분은 대상이 아니에요',
        disabledReason: !props.orderId
          ? '주문 정보가 없는 화면에서는 발송할 수 없습니다.'
          : usageGuideTargets.length === 0
            ? '이용안내는 이미 신청·받은 분들께 보내는 사후 안내라, 교환신청·발송완료 상태이면서 신청자 번호가 있는 쿠폰이 없으면 보낼 수 없습니다.\n수령자가 교환신청을 하거나 대리발송을 하면 활성화됩니다.'
            : null,
        onClick: () => setShowUsageGuideModal(true),
        enabledClassName: 'border-indigo-200 text-indigo-700 hover:bg-indigo-50',
      });
    }
    return actions;
  }, [
    isAdmin,
    isGiftAgent,
    sortedCoupons,
    phoneFormTargets,
    deliveredPhoneTargets,
    proxyDeliverTargets,
    usageGuideTargets,
    props.order,
    props.orderId,
    courtesyEligibleCoupons,
    setShowCourtesyModal,
  ]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-[99vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <CouponDetailHeader
          stats={state.stats}
          orderId={props.orderId}
          order={props.order}
          previewCopied={state.previewCopied}
          setPreviewCopied={state.setPreviewCopied}
          onClose={onClose}
          actions={headerActions}
          extraActions={
            props.orderId ? (
              <CsMemoButton
                variant="button"
                label="CS메모"
                target={{ giftOrder: props.orderId }}
                title="주문 CS 응대 메모"
                subtitle={csMemoSubtitle}
                memos={csMemoSplit.all}
                isLoading={csMemoQuery.isLoading}
                unavailable={csMemoUnavailable}
                subLabelMap={csMemoSubLabelMap}
                notice="주문·쿠폰·배송 메모 통합 · 입력은 주문 단위로 저장"
              />
            ) : undefined
          }
        />

        <ResultBanners
          cancelResult={state.cancelResult}
          additionalResult={state.additionalResult}
          disposeResult={state.disposeResult}
          refundResult={state.refundResult}
        />

        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
          <CrossCouponOverlapBanner
            overlap={state.crossCouponHashOverlap}
            couponMap={couponMap}
            giftItemEmbedMap={state.giftItemEmbedMap}
            onOpenKakaoShareCallbacks={state.openKakaoCallbackModal}
            onOpenShareHistory={state.openShareHistoryModal}
          />

          <div className="mb-3 grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
            {state.canCancel && state.cancellableCoupons.length > 0 && (
              <CancelSection
                cancellableCoupons={state.cancellableCoupons}
                selectedIds={state.selectedIds}
                isFullCancel={state.isFullCancel}
                isManualRefund={state.isManualRefund}
                giftItemEmbedMap={state.giftItemEmbedMap}
                toggleSelectAll={state.toggleSelectAll}
                onRequestCancel={state.openCancelFlow}
              />
            )}

            {state.canCancel && state.reissueStatsByItem.length > 0 && (
              <ReissueSection
                reissueStatsByItem={state.reissueStatsByItem}
                totalReissueRemaining={state.totalReissueRemaining}
                showAdditionalForm={state.showAdditionalForm}
                setShowAdditionalForm={state.setShowAdditionalForm}
                reissueGiftItemId={state.reissueGiftItemId}
                setReissueGiftItemId={state.setReissueGiftItemId}
                additionalCount={state.additionalCount}
                setAdditionalCount={state.setAdditionalCount}
                selectedReissueStat={state.selectedReissueStat}
                isCreatingAdditional={state.isCreatingAdditional}
                handleCreateAdditional={state.handleCreateAdditional}
                reissueOverride={state.reissueOverride}
                requestReissueOverride={state.requestReissueOverride}
              />
            )}

            {state.canCancel && (
              <DisposeSection
                disposeMode={state.disposeMode}
                setDisposeMode={state.setDisposeMode}
                disposeAction={state.disposeAction}
                setDisposeAction={state.setDisposeAction}
                disposeSelectedIds={state.disposeSelectedIds}
                setDisposeSelectedIds={state.setDisposeSelectedIds}
                setShowDisposeConfirm={state.setShowDisposeConfirm}
                disposableCoupons={state.disposableCoupons}
                toggleDisposeSelectAll={state.toggleDisposeSelectAll}
              />
            )}

            {isAdmin() && props.orderId && (
              <BillingCardSection orderId={props.orderId} />
            )}
          </div>

          <CouponTable
            sortedCoupons={state.sortedCoupons}
            giftItemEmbedMap={state.giftItemEmbedMap}
            canCancel={state.canCancel}
            hasCancellableCoupons={state.cancellableCoupons.length > 0}
            disposeMode={state.disposeMode}
            selectedIds={state.selectedIds}
            disposeSelectedIds={state.disposeSelectedIds}
            isPromoting={state.isPromoting}
            crossCouponOverlapIds={state.crossCouponHashOverlap.overlapCouponIds}
            toggleSelect={state.toggleSelect}
            toggleDisposeSelect={state.toggleDisposeSelect}
            onOpenShareHistory={state.openShareHistoryModal}
            handlePromoteToReissued={state.handlePromoteToReissued}
            onIssueRefund={state.handleIssueRefund}
            onIssueRenewal={state.handleIssueRenewal}
            onCancelIssue={state.handleCancelIssue}
            onSendRefundNotice={
              state.canSendRefundNotice ? state.openRefundNotice : undefined
            }
            onRecordRenewal={
              state.canRecordRenewal ? state.openRenewalRecord : undefined
            }
            onCancelRenewal={
              state.canRecordRenewal ? state.handleCancelRenewal : undefined
            }
            onOpenKakaoShareCallbacks={state.openKakaoCallbackModal}
            isIssuing={state.isIssuing}
            onResetShareStatus={state.handleResetShareStatus}
            isResettingShare={state.isResettingShare}
            onUpdateAdditionalPhone={state.handleUpdateAdditionalPhone}
            onOpenExtraRedeem={state.openExtraRedeemModal}
            onSpawnPay={state.openSpawnPayModal}
            onSpawnFulfill={state.openSpawnFulfillModal}
            onSpawnCancel={state.handleCancelSpawn}
            cancelingSpawnId={state.cancelingSpawnId}
            csMemoByCoupon={props.orderId ? csMemoSplit.byCoupon : undefined}
            csMemoLoading={csMemoQuery.isLoading}
            csMemoUnavailable={csMemoUnavailable}
            csMemoSubtitle={csMemoSubtitle}
            highlightCouponId={props.highlightCouponId}
          />
        </div>

        {state.showDisposeConfirm && (
          <DisposeConfirmDialog
            disposeAction={state.disposeAction}
            disposeSelectedIds={state.disposeSelectedIds}
            disposeReason={state.disposeReason}
            setDisposeReason={state.setDisposeReason}
            isDisposing={state.isDisposing}
            setShowDisposeConfirm={state.setShowDisposeConfirm}
            handleDispose={state.handleDispose}
          />
        )}

        {state.showCancelConfirm && (
          <CancelConfirmDialog
            isFullCancel={state.isFullCancel}
            hasDeliveryItems={state.hasDeliveryItems}
            selectedIds={state.selectedIds}
            cancelReason={state.cancelReason}
            setCancelReason={state.setCancelReason}
            isCancelling={state.isCancelling}
            setShowCancelConfirm={state.setShowCancelConfirm}
            handleCancelRequest={state.handleCancelRequest}
          />
        )}

        {state.showManualRefundModal && (
          <ManualRefundCancelDialog
            isFullCancel={state.isFullCancel}
            selectedCount={state.selectedIds.size}
            method={props.order?.paymentDetail?.method}
            expectedRefundAmount={state.expectedRefundAmount}
            isCancelling={state.isCancelling}
            onClose={() => state.setShowManualRefundModal(false)}
            onConfirm={state.handleManualRefundCancel}
          />
        )}
      </div>

      {state.showCourtesyModal && (
        <CourtesyMessageModal
          coupons={state.courtesyEligibleCoupons}
          order={props.order}
          giftItemEmbedMap={state.giftItemEmbedMap}
          onClose={() => state.setShowCourtesyModal(false)}
          onSent={() => state.setShowCourtesyModal(false)}
        />
      )}

      {issueConfirmTarget && (
        <IssueLinkConfirmModal
          target={issueConfirmTarget}
          followsNotice={issueConfirmTarget.type === 'refund' && state.canSendRefundNotice}
          onConfirm={state.confirmIssue}
          onCancel={state.cancelIssueConfirm}
        />
      )}

      {refundNoticeTarget && (
        <RefundLinkNoticeModal
          target={refundNoticeTarget}
          onClose={state.closeRefundNotice}
        />
      )}

      {renewalRecordTarget && (
        <RenewalRecordModal
          target={renewalRecordTarget}
          onClose={state.closeRenewalRecord}
        />
      )}

      {showUsageGuideModal && props.orderId && (
        <UsageGuideSendModal
          giftOrderId={props.orderId}
          targets={usageGuideTargets}
          onClose={() => setShowUsageGuideModal(false)}
        />
      )}

      {showProxyDeliverModal && (
        <ProxyDeliverModal
          targets={proxyDeliverTargets}
          orderCoupons={sortedCoupons}
          giftItemEmbedMap={state.giftItemEmbedMap}
          order={props.order}
          onClose={() => setShowProxyDeliverModal(false)}
        />
      )}

      {/* 기입폼 업로드 파일 선택 (숨김) — 같은 파일 재선택 가능하게 value 초기화 */}
      <input
        ref={phoneFormFileRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handlePhoneFormFile(file);
        }}
      />

      {phoneFormParsed && (
        <PhoneFormUploadModal
          parsed={phoneFormParsed}
          giftItemEmbedMap={state.giftItemEmbedMap}
          onClose={(didApply) => {
            setPhoneFormParsed(null);
            // 쿠폰 목록은 부모(주문 목록)에서 내려오는 스냅샷이라 저장분 반영은 새로고침으로.
            if (didApply) window.location.reload();
          }}
        />
      )}

      {state.kakaoCallbackCoupon && (
        <KakaoShareCallbackModal
          coupon={state.kakaoCallbackCoupon}
          onClose={state.closeKakaoCallbackModal}
        />
      )}

      {state.shareHistoryCoupon && (
        <CouponShareHistoryModal
          coupon={state.shareHistoryCoupon}
          onClose={state.closeShareHistoryModal}
        />
      )}

      {state.extraRedeemCoupon && (
        <ExtraRedeemModal
          coupon={state.extraRedeemCoupon}
          onClose={state.closeExtraRedeemModal}
          onChanged={() => window.location.reload()}
        />
      )}

      {state.spawnPayCoupon && (
        <ExtraPayModal
          couponId={state.spawnPayCoupon._id}
          couponCode={state.spawnPayCoupon.code}
          productLabel={
            state.giftItemEmbedMap.get(state.spawnPayCoupon.giftItem)?.name ??
            state.spawnPayCoupon.code
          }
          onPay={state.handleSpawnPay}
          onClose={state.closeSpawnPayModal}
        />
      )}

      {state.spawnFulfillCoupon && (
        <ExtraFulfillModal
          couponId={state.spawnFulfillCoupon._id}
          couponCode={state.spawnFulfillCoupon.code}
          productLabel={
            state.giftItemEmbedMap.get(state.spawnFulfillCoupon.giftItem)?.name ??
            state.spawnFulfillCoupon.code
          }
          getDonors={state.getDonorCandidates}
          onFulfill={state.handleSpawnFulfill}
          onClose={state.closeSpawnFulfillModal}
        />
      )}
    </div>
  );
};
